import axios from "axios";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "src/app/components/CustomInput/CustomInput";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Modal, { ModalTitle } from "src/app/components/Modal/Modal";
import generateUuid from "src/app/libs/generateUuid";
import { translateMessage } from "src/app/methods/translateMessage";
import { RootState } from "src/redux/reducers";
import { API_URLS } from "src/utils/API_URLS";
import { showErrorToast } from "src/utils/methods";
import { SocialInsightsDashboardListElement } from "src/redux/insights-dashboard/utils";
import { getDashboardList } from "src/redux/insights-dashboard/insightsDashboardActions";
import { showToast } from "src/app/methods/showToast";

import "./DuplicateDashboardModal.scss";

interface Props {
  data: SocialInsightsDashboardListElement;
  onClose: () => void;
}

export const DuplicateDashboardModal = (props: Props) => {
  const { data, onClose } = props;

  const [name, setName] = useState(`Copy of ${data.name}`);
  const [loading, setLoading] = useState(false);

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const dispatch = useDispatch();
  const intl = useIntl();
  const isNameTooLong = name.length > 128;

  const submit = async () => {
    try {
      setLoading(true);

      await axios.post(API_URLS.createInsightsDashboard, {
        uuid: generateUuid(),
        name,
        filters: data.filters,
        wsWorkspaceUuid: activeWorkspaceUuid,
      });

      dispatch(getDashboardList(activeWorkspaceUuid));
      showToast(
        "success",
        <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
        <IDHFormattedMessage
          id="ws_dashboard_duplicated_successfully"
          defaultMessage="Dashboard has been duplicated successfully"
        />,
      );
      onClose();
    } catch (err) {
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="duplicate-dashboard-modal"
      overlayClassName="duplicate-dashboard-modal__overlay"
      onClose={onClose}
      closeButtonText={
        <IDHFormattedMessage id="ws_cancel" defaultMessage="Cancel" />
      }
      displayCancelButton
      closeButtonVariant="white-with-light-grey-border"
      confirmButtonDisabled={name.trim().length < 3 || isNameTooLong}
      confirmButtonVariant="blue"
      confirmButtonText={
        <IDHFormattedMessage id="ws_save" defaultMessage="Save" />
      }
      confirmButtonLoading={loading}
      onConfirmClick={submit}
    >
      <ModalTitle>
        <IDHFormattedMessage
          id="ws_duplicate_dashboard"
          defaultMessage="Duplicate dashboard"
        />
      </ModalTitle>
      <CustomInput
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={isNameTooLong}
        placeholder={translateMessage({
          intl,
          id: "ws_enter_name",
          defaultMessage: "Enter name",
        })}
      />
      {isNameTooLong && (
        <div className="duplicate-dashboard-modal__validation-message">
          <IDHFormattedMessage
            id="ws_dashboard_name_max_128_characters"
            defaultMessage="Dashboard name cannot exceed 128 characters"
          />
        </div>
      )}
    </Modal>
  );
};
